import { useSyncExternalStore } from "react";
import type { OrderModelV2 } from "@/models/orders/OrderModelV2";

// 「이 브라우저가 방금 GE 결제를 끝냈다」는 기록 — 주문 목록(`/tracking`)이 새 주문을 기다려 다시 읽게 한다. #6140.
//
// 왜 필요한가: GE 는 완료 화면을 **먼저** 보여 주고 우리 주문 생성(SendOrderToMerchant)은 그 뒤에 온다. 2026-10-30 dev
// `GE188658017US`(Google Pay): 완료 뒤 사용자가 주문 목록을 연 시각 20:33:28 KST, SendOrder 20:33:32, 확정 20:33:41 —
// 목록이 주문 생성 4초 **전에** 읽혀 비어 보였고 다시 읽지 않았다. PayPal 은 복귀 뒤 SendOrder 까지 46~55초 걸린 실측도
// 있다(`consumed-cart.ts` `RETURN_POLL_MAX_MS` 주석).
//
// 쓰는 곳: GE CONFIRMATION(결제 성공) 콜백(`checkout-analytics.ts`) 한 곳 — GE 주문번호를 아는 곳은 거기뿐이다.
// 읽는 곳: `useGetOrders`(로그인 주문 목록). 못 읽으면(스토리지 차단·형식 이상) 없는 것으로 본다 = 종전 동작(1회 조회).

export const RECENT_GE_ORDER_KEY = "bh-ge-recent-order";
/** 결제 뒤 이 시간 동안만 다시 읽는다. PayPal 복귀 뒤 SendOrder 실측 최대 55초를 넉넉히 덮는다. */
export const RECENT_GE_ORDER_POLL_MS = 3 * 60 * 1000;
/** 다시 읽는 간격. */
export const RECENT_GE_ORDER_POLL_INTERVAL_MS = 5000;
/**
 * 빈 목록 대신 로딩을 보이는 시간 — 폴링(3분)보다 짧게 끊는다. 기록은 로그인 여부와 상관없이 남아서, 게스트로 결제한 뒤
 * 로그인한 사람(게스트 주문은 계정에 안 붙는다)에게는 「로그아웃하고 조회하라」가 맞는 안내인데 로딩이 그걸 가린다(#6154 R2).
 * 90초 = PayPal 복귀 뒤 SendOrder 실측 최대 55초 + 주문 확정까지 9초(`GE188658017US`) + 폴링 간격 5초를 덮는 값.
 * 이 시간이 지나면 안내가 나오고, 폴링은 3분까지 이어져 주문이 오면 목록으로 바뀐다.
 */
export const RECENT_GE_ORDER_AWAIT_UI_MS = 90 * 1000;

export interface RecentGeOrder {
  /** GE 주문번호(예: `GE188658017US`) — 우리 주문의 `paygateTransactionId` 에 들어간다(`send-order.ts`). */
  geOrderId: string;
  /** 결제 완료(CONFIRMATION)를 받은 시각(ms, 브라우저 시계). */
  at: number;
}

function storage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

export function markRecentGeOrder(geOrderId: string, now = Date.now()): void {
  if (!geOrderId) return;
  try {
    storage()?.setItem(
      RECENT_GE_ORDER_KEY,
      JSON.stringify({ geOrderId, at: now } satisfies RecentGeOrder),
    );
  } catch (e) {
    console.warn("[GlobalE] recent order marker not persisted", e);
  }
}

export function clearRecentGeOrder(): void {
  try {
    storage()?.removeItem(RECENT_GE_ORDER_KEY);
  } catch {
    /* noop */
  }
}

/** 기다리는 창 안의 기록만 돌려준다. 창이 지났거나 형식이 이상하면 지우고 null. */
export function readRecentGeOrder(now = Date.now()): RecentGeOrder | null {
  let parsed: Partial<RecentGeOrder> | null = null;
  try {
    const raw = storage()?.getItem(RECENT_GE_ORDER_KEY);
    if (!raw) return null;
    parsed = JSON.parse(raw) as Partial<RecentGeOrder>;
  } catch {
    parsed = null;
  }
  const ok =
    !!parsed &&
    typeof parsed.geOrderId === "string" &&
    parsed.geOrderId.length > 0 &&
    typeof parsed.at === "number" &&
    now - parsed.at >= 0 &&
    now - parsed.at <= RECENT_GE_ORDER_POLL_MS;
  if (!ok) {
    clearRecentGeOrder();
    return null;
  }
  return parsed as RecentGeOrder;
}

/**
 * 받은 목록에 방금 결제한 주문이 보이나. 둘 중 하나면 보인 것으로 본다:
 *   - GE 주문번호가 같은 주문(`paygateTransactionId`)
 *   - 이 기록이 생긴 뒤 **처음 읽은 목록(`baselineIds`)에 없던** 주문 — 목록 응답이 `paygateTransactionId` 를 안 싣는
 *     경우에도 멈추게 하는 보조 조건. 기준 목록이 없으면(첫 응답 전) 이 조건은 쓰지 않는다.
 */
export function hasRecentGeOrder(
  orders: readonly OrderModelV2[] | undefined,
  recent: RecentGeOrder,
  baselineIds: ReadonlySet<string> | null,
): boolean {
  if (!orders) return false;
  return orders.some(
    (o) =>
      o.paygateTransactionId === recent.geOrderId ||
      (baselineIds !== null && !baselineIds.has(o.id)),
  );
}

/** 로딩을 보일 창(`RECENT_GE_ORDER_AWAIT_UI_MS`) 안의 기록 시각(ms) — 없거나 창이 지났으면 null. 기록은 지우지 않는다(폴링이 쓴다). */
function readAwaitingUiAt(now = Date.now()): number | null {
  const recent = readRecentGeOrder(now);
  return recent && now - recent.at <= RECENT_GE_ORDER_AWAIT_UI_MS
    ? recent.at
    : null;
}

/**
 * 빈 목록 대신 로딩을 보일 창 안의 기록 시각(ms) — 없으면 null. 서버 렌더와 첫 하이드레이션은 null(스토리지 없음)이고,
 * 창이 끝나는 순간 다시 그리도록 그 시각에 타이머를 건다. 다른 탭이 기록을 바꾸면(`storage` 이벤트) 다시 읽는다.
 */
export function useRecentGeOrderAwaitingAt(): number | null {
  return useSyncExternalStore(
    subscribeRecentGeOrder,
    () => readAwaitingUiAt(),
    () => null,
  );
}

function subscribeRecentGeOrder(onChange: () => void): () => void {
  const at = readAwaitingUiAt();
  const timer =
    at !== null
      ? setTimeout(
          onChange,
          Math.max(0, at + RECENT_GE_ORDER_AWAIT_UI_MS - Date.now()) + 1,
        )
      : undefined;
  window.addEventListener("storage", onChange);
  return () => {
    if (timer) clearTimeout(timer);
    window.removeEventListener("storage", onChange);
  };
}
